import cx from 'clsx';
import { addMinutes, format, getTime, isAfter, isBefore, isEqual } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as InfoIcon } from '@assets/svg/ic-info.svg';

import LoaderGif from '@components/common/LoaderGif/LoaderGif';
import DatePicker from '@components/form/DatePicker/DatePicker';
import RadioButtonsGroup from '@components/form/RadioButtonsGroup/RadioButtonsGroup';
import TextArea from '@components/form/TextArea/TextArea';
import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IEquipmentItem } from '@pages/ManageEquipment/model/equipmentsList';
import { useEquipmentsDeactivateMutation } from '@pages/ManageEquipment/services/useEquipmentsDeactivateMutation';
import {
  selectManageEquipmentActiveEquipment,
  selectManageEquipmentSelectedAllValue,
  selectManageEquipmentSelectedRows,
  useManageEquipmentActions,
} from '@pages/ManageEquipment/store/useManageEquipmentStore';

import { useEquipmentsCheckQuery } from '@services/equipment/useEquipmentsCheckQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import ActiveActionItems from './ActiveActionItems';
import {
  EQUIPMENT_DEACTIVATE_FIELDS,
  equipmentDeactivateDefaultValue,
  schemaEquipmentDeactivate,
} from './constants/schemaEquipmentDeactivate';
import style from './style.module.scss';

interface IProps {
  selectedEquipments: Array<IEquipmentItem>;
}

const EquipmentDeactivateModal: React.FC<IProps> = ({ selectedEquipments }) => {
  const { t } = useTranslation();
  const { data: profileData } = useUserProfileQuery();
  const { setModalClose } = useModalActions();
  const selectedAll = selectManageEquipmentSelectedAllValue();
  const selectedRows = selectManageEquipmentSelectedRows();
  const activeEquipment: Array<IEquipmentItem> = selectManageEquipmentActiveEquipment();
  const { setEquipmentSelectedRows, setEquipmentSelectedAll, setActiveEquipment } =
    useManageEquipmentActions();

  const facilityOffset = selectedEquipments[0].facility_offset || 0;
  const utcTime = formatInTimeZone(new Date(), 'UTC', 'yyyy/MM/dd HH:mm');
  const currentFacilityDateTime = addMinutes(utcTime, facilityOffset * 60);
  const facilityTimecaption = format(getTime(currentFacilityDateTime), 'HH:mm');

  const {
    IS_INDEFINITELY,
    DEACTIVATE_DATE,
    DEACTIVATE_RANGE_START,
    DEACTIVATE_RANGE_END,
    DEACTIVATE_REASON,
    REMIND_DATE,
  } = EQUIPMENT_DEACTIVATE_FIELDS;

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm({
    mode: 'onChange',
    resolver: schemaEquipmentDeactivate,
    defaultValues: equipmentDeactivateDefaultValue(currentFacilityDateTime),
  });

  const [isIndefinitely, deactivateRangeStart, deactivateRangeEnd, remindDate] = watch([
    IS_INDEFINITELY,
    DEACTIVATE_RANGE_START,
    DEACTIVATE_RANGE_END,
    REMIND_DATE,
  ]);

  const { data: equipmentsCheckData, isLoading: equipmentsCheckIsLoading } = useEquipmentsCheckQuery({
    equipments: selectedEquipments,
  });
  const { mutate: equipmentsDeactivateMutate, isLoading } = useEquipmentsDeactivateMutation();

  const isErrorFree = !!Object.keys(errors).length;

  const submit = handleSubmit(
    (data) => {
      const deactivateDate = isIndefinitely ? data.deactivate_date : data.deactivate_range_start;

      if (
        data.remind_date &&
        (isBefore(data.remind_date, deactivateDate) || isAfter(data.remind_date, data.deactivate_range_end))
      ) {
        return setError(REMIND_DATE, { message: 'Enter correct date' });
      }

      if (!!isIndefinitely && !!equipmentsCheckData) {
        return setError('OpenedActionItems', { type: 'disabled' });
      }

      return equipmentsDeactivateMutate(
        {
          equipments: selectedEquipments.map((s) => s.id),
          start_date: format(addMinutes(deactivateDate, -facilityOffset * 60), 'yyyy/MM/dd HH:mm'),
          end_date: isIndefinitely
            ? undefined
            : format(addMinutes(data.deactivate_range_end, -facilityOffset * 60), 'yyyy/MM/dd HH:mm'),
          remind_date:
            isIndefinitely || !data.remind_date
              ? undefined
              : format(addMinutes(data.remind_date, -facilityOffset * 60), 'yyyy/MM/dd HH:mm'),
          deactivate_reason: data.deactivate_reason,
        },
        {
          onSuccess: () => {
            showToast({ variant: 'success', message: t('Your changes have been saved.') });

            if (selectedAll) {
              setEquipmentSelectedAll(false);
            }
            if (selectedEquipments.length > 1) {
              const rows = { ...selectedRows };
              selectedEquipments.forEach((eq) => {
                delete rows[eq.id];
              });
              setEquipmentSelectedRows(rows);
              setActiveEquipment([]);
            } else {
              const rows = { ...selectedRows };
              delete rows[selectedEquipments[0].id];
              setEquipmentSelectedRows(rows);
              setActiveEquipment(activeEquipment.filter((eq) => eq.id !== selectedEquipments[0].id));
            }

            closeModal();
          },
        },
      );
    },
    (errors) => console.error(errors),
  );

  const filterPassedTime = (time: Date, startDate: Date, field?: 'start' | 'end', endDate?: Date) => {
    const selectedDate = new Date(time);

    const isEndField = field === 'end';
    const hasStartDate = !!startDate;
    const isAfterStartDate = hasStartDate && selectedDate.getTime() > startDate.getTime();
    const isFuture = selectedDate.getTime() > currentFacilityDateTime.getTime();

    if (!isIndefinitely && isEndField && hasStartDate) {
      if (endDate) {
        const isBeforeEndDate = selectedDate.getTime() < endDate.getTime();

        return isAfterStartDate && isBeforeEndDate;
      }

      return isAfterStartDate;
    }

    return isFuture;
  };

  const closeModal = () => {
    setModalClose();
    reset();
  };

  const openAiCount =
    (equipmentsCheckData?.regularList.length || 0) + (equipmentsCheckData?.hardwareList.length || 0);

  return (
    <div className={style.equipmentDeactivateModal}>
      <ModalHeader
        title={`${t('Deactivate Equipment')} ${
          selectedEquipments.length === 1 ? `"${selectedEquipments[0].name}"` : ''
        } `}
      />
      <div className={style.modalBody}>
        {equipmentsCheckIsLoading ? (
          <LoaderGif className={style.loader} />
        ) : (
          !!equipmentsCheckData && (
            <div className={style.openAiBlock}>
              {t(`There ${openAiCount === 1 ? 'is' : 'are'} open action item${openAiCount === 1 ? '' : 's'}`)}{' '}
              (<span className={style.openAiLink}>{openAiCount}</span>):
              <ActiveActionItems
                item={equipmentsCheckData.regularList[0]}
                length={equipmentsCheckData.regularList.length}
                label={t('Regular Action Items')}
              />
              <ActiveActionItems
                item={equipmentsCheckData.hardwareList[0]}
                length={equipmentsCheckData.hardwareList.length}
                label={t('Hardware Action Items')}
              />
              <p
                className={cx(style.warningText, {
                  [style.warningTriggeredText]: !!isIndefinitely && isErrorFree,
                })}
              >
                {isIndefinitely
                  ? t(
                      `Close action items before deactivation or set an end date for automatic 'Snooze'.`,
                      {},
                      true,
                    )
                  : t(
                      `All open action items will have their status changed to 'Snooze'.Once the equipment is activated, the status will change to 'Needs Response'.`,
                      {},
                      true,
                    )}
              </p>
            </div>
          )
        )}
        <RadioButtonsGroup
          name={EQUIPMENT_DEACTIVATE_FIELDS.IS_INDEFINITELY}
          control={control}
          options={[
            { value: 0, label: t('Deactivate for a Time Period') },
            { value: 1, label: t('Deactivate Indefinitely') },
          ]}
          size="small"
        />
        <div className={style.datePickerBlock}>
          {isIndefinitely ? (
            <div className={style.dateTimeBlock}>
              <DatePicker
                control={control}
                name={DEACTIVATE_DATE}
                selectsStart
                required
                placeholderText={t('Start date')}
                dateFormat={`${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`}
                minDate={new Date()}
                filterTime={(time) => filterPassedTime(time, deactivateRangeStart)}
                showMonthDropdown
                showYearDropdown
                timeCaption={facilityTimecaption}
                showTimeSelect
              />
            </div>
          ) : (
            <>
              <div className={style.dateTimeBlock}>
                <DatePicker
                  selected={deactivateRangeStart}
                  onChange={(date) => {
                    setValue(DEACTIVATE_RANGE_START, date);
                    if (isEqual(date, deactivateRangeEnd) || isAfter(date, deactivateRangeEnd)) {
                      setValue(DEACTIVATE_RANGE_END, date);
                    }
                  }}
                  selectsStart
                  placeholderText={t('Start date')}
                  dateFormat={`${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`}
                  startDate={deactivateRangeStart}
                  endDate={deactivateRangeEnd}
                  minDate={new Date()}
                  filterTime={(time) => filterPassedTime(time, deactivateRangeStart, 'start')}
                  showMonthDropdown
                  showYearDropdown
                  showTimeSelect
                  timeCaption={facilityTimecaption}
                  type={'woHelperText'}
                />
              </div>
              <span className={style.dateRangeSeparator}>-</span>
              <div className={style.dateTimeBlock}>
                <DatePicker
                  control={control}
                  name={DEACTIVATE_RANGE_END}
                  selectsEnd
                  placeholderText={t('End date')}
                  dateFormat={`${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`}
                  minDate={deactivateRangeStart}
                  filterTime={(time) => filterPassedTime(time, deactivateRangeStart, 'end')}
                  disabled={!deactivateRangeStart}
                  showMonthDropdown
                  showYearDropdown
                  showTimeSelect
                  timeCaption={facilityTimecaption}
                  type={'woHelperText'}
                />
              </div>
            </>
          )}
          <InfoIcon
            data-tooltip-id="info-icon"
            className={style.infoIcon}
            data-tooltip-content={t('Original time based on facility time zone')}
          />
          {!isIndefinitely && (
            <div className={cx(style.dateTimeBlock, style.reminderBlock)}>
              <DatePicker
                label={t('Reminder date')}
                control={control}
                name={REMIND_DATE}
                placeholderText={t('Reminder date')}
                dateFormat={`${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`}
                minDate={deactivateRangeStart}
                maxDate={deactivateRangeEnd}
                filterTime={(time) => filterPassedTime(time, deactivateRangeStart, 'end', deactivateRangeEnd)}
                disabled={!deactivateRangeStart || !deactivateRangeEnd}
                showMonthDropdown
                showYearDropdown
                showTimeSelect
                timeCaption={facilityTimecaption}
              />
              <InfoIcon
                data-tooltip-id="info-icon"
                className={style.reminderInfoIcon}
                data-tooltip-content={t('Select a date to receive an email reminder before reactivation')}
              />
            </div>
          )}
        </div>
        <div className={style.reasonBlock}>
          <TextArea label={t('Deactivation reason')} control={control} name={DEACTIVATE_REASON} required />
        </div>
        <Tooltip id="info-icon" />
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Cancel')} onClick={closeModal} />
        <Button
          type="primary"
          variant="medium"
          disabled={isLoading || equipmentsCheckIsLoading}
          text={t('Deactivate')}
          onClick={submit}
        />
      </div>
    </div>
  );
};

export default EquipmentDeactivateModal;
